import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { format } from "date-fns";
import {
  ArrowLeft,
  CalendarDays,
  Clock3,
  Copy,
  Globe,
  Lock,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  List,
  SquarePen,
  UserRound,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ActivityLogTable } from "@/components/activity/ActivityLogTable";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { dataApi, userApi, type User, type UserRoleAssignment } from "@/lib/api/admin-api";
import { cn } from "@/lib/utils";
import { EditUserDialog, UserAccessControlTab, UserResetPasswordTab } from "@/components/users";

export const Route = createFileRoute("/(protected)/_layout/users_/$userId")({
  component: UserDetailPage,
});

type Tab = "general" | "access" | "resetPassword" | "activity";

function getUserName(user: User): string {
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name || user.email;
}

function getUserInitials(user: User): string {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || user.email.slice(0, 2).toUpperCase();
}

function getPrimaryRole(user: User, roles: UserRoleAssignment[]): string {
  const firstRole =
    roles.find((item) => item.role.code !== "SUPER_ADMIN") ??
    roles[0] ??
    user.userRoles?.find((item) => item.role.code !== "SUPER_ADMIN") ??
    user.userRoles?.[0];

  return firstRole?.role.name ?? "-";
}

function safeFormatDate(value?: string, pattern = "dd MMM yyyy"): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return format(date, pattern);
}

function UserDetailPage() {
  const { t } = useLingui();
  const { isAuthenticated, isLoading: isAuthLoading, hasPermission } = useAuth();
  const { userId } = useParams({
    from: "/(protected)/_layout/users_/$userId",
  });
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [editOpen, setEditOpen] = useState(false);
  const isTablet = useIsTablet();

  const userIdNum = Number(userId);
  const isValidId = Number.isFinite(userIdNum) && userIdNum > 0;

  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ["users", userIdNum],
    queryFn: () => dataApi.getUser(String(userIdNum)),
    enabled: isAuthenticated && isValidId,
  });

  const { data: roles = [], isLoading: isLoadingRoles } = useQuery({
    queryKey: ["users", userIdNum, "roles"],
    queryFn: () => userApi.getRoles(String(userIdNum)),
    enabled: isAuthenticated && isValidId,
  });

  const { data: locations = [], isLoading: isLoadingLocations } = useQuery({
    queryKey: ["users", userIdNum, "locations"],
    queryFn: () => userApi.getAccessibleLocations(String(userIdNum)),
    enabled: isAuthenticated && isValidId,
  });

  const isLoading = isAuthLoading || isLoadingUser || isLoadingRoles || isLoadingLocations;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 p-3">
        <Skeleton className="h-6 w-40" />
        <div className="flex items-center gap-6 px-3">
          <Skeleton className="size-28 rounded-2xl md:size-40" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-5 w-56" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, idx) => (
            <Skeleton key={idx} className="h-23 w-full" />
          ))}
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-col gap-5 px-4">
          {Array.from({ length: 8 }).map((_, idx) => (
            <Skeleton key={idx} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <UserRound className="size-12 text-(--color-text-secondary)/30" />
        <p className="text-(--color-text-secondary)">{t`User not found`}</p>
        <Link
          to="/users"
          search={{ page: 1, pageSize: 50, status: "all" }}
          className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm"
        >
          {t`Back to Users`}
        </Link>
      </div>
    );
  }

  const role = getPrimaryRole(user, roles);
  const regionNames = Array.from(
    new Set(
      locations
        .map((item) => item.region?.name)
        .filter((name): name is string => Boolean(name)),
    ),
  );
  const locationNames = Array.from(new Set(locations.map((item) => item.name).filter(Boolean)));
  const permissionsSummary = roles.length > 0 ? t`Role-based` : t`No roles assigned`;
  const recipesCreated =
    ((user as { _count?: { recipes?: number } })._count?.recipes ??
      (user as { recipesCount?: number }).recipesCount ??
      0);

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: t`General Details` },
    { id: "access", label: t`Access` },
    { id: "resetPassword", label: t`Reset password` },
    { id: "activity", label: t`Activity Log` },
  ];

  const canEditUser = hasPermission("user:update");
  const canEditPermissions = hasPermission("user:update");

  return (
    <div className="flex flex-col gap-5 p-3">
      {isTablet && (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <Link
              to="/users"
              search={{ page: 1, pageSize: 50, status: "all" }}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
              title={t`Back`}
            >
              <ArrowLeft className="size-5" />
            </Link>
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {getUserName(user)}
            </h1>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold shrink-0",
                user.isActive
                  ? "bg-(--color-success-bg) text-(--color-success-text)"
                  : "bg-(--color-surface-muted) text-(--color-text-secondary)",
              )}
            >
              {user.isActive ? t`Active` : t`Inactive`}
            </span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <Button variant="outline" className="h-10 gap-2 rounded-[10px] border-(--shell-border) px-4" disabled>
                <MoreHorizontal className="size-5" />
                <span className="text-base font-medium">{t`More`}</span>
              </Button>
              <Button
                className="h-10 gap-2 rounded-[10px] bg-(--color-action) px-4 text-(--color-action-fg) hover:bg-(--color-action)/90"
                onClick={() => setEditOpen(true)}
                disabled={!canEditUser}
              >
                <SquarePen className="size-5" />
                <span className="text-base font-medium">{t`Edit user`}</span>
              </Button>
            </div>
          </div>
        </PageHeader>
      )}
      {!isTablet && (
        <div className="flex flex-col gap-2">
          <Link
            to="/users"
            search={{ page: 1, pageSize: 50, status: "all" }}
            className="flex w-fit items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-(--color-surface-accent)"
          >
            <ArrowLeft className="size-5 text-(--color-text-secondary)" />
            <span className="text-base font-medium text-(--color-text-secondary)">{t`Back to Users`}</span>
          </Link>

          <div className="flex flex-col gap-5 px-3 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
              <div className="flex size-28 shrink-0 items-center justify-center rounded-2xl border-4 border-white bg-(--color-avatar-magenta-bg) shadow-lg md:size-40">
                <span className="text-4xl font-medium tracking-tight text-(--color-avatar-magenta) md:text-[60px]">
                  {getUserInitials(user)}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-4xl font-semibold leading-none text-(--color-text-primary) md:text-6xl">
                    {getUserName(user)}
                  </h1>
                  <Badge
                    className={cn(
                      "rounded-full border-transparent px-3 py-1 text-sm font-medium",
                      user.isActive
                        ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                        : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
                    )}
                  >
                    {user.isActive ? t`Active` : t`Inactive`}
                  </Badge>
                </div>
                <p className="text-base text-(--color-text-secondary)">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" className="h-13 gap-2 rounded-[10px] border-(--shell-border) px-4" disabled>
                <MoreHorizontal className="size-5" />
                <span className="text-base font-medium">{t`More`}</span>
              </Button>
              <Button
                className="h-13 gap-2 rounded-[10px] bg-(--color-action) px-4 text-(--color-action-fg) hover:bg-(--color-action)/90"
                onClick={() => setEditOpen(true)}
                disabled={!canEditUser}
              >
                <SquarePen className="size-5" />
                <span className="text-base font-medium">{t`Edit user`}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 rounded-xl bg-(--color-surface-muted) p-2 md:grid-cols-2 xl:grid-cols-4">
        <InfoCard icon={<UserRound className="size-5 text-(--color-text-secondary)" />} label={t`Role`} value={role} />
        <InfoCard
          icon={<Clock3 className="size-5 text-(--color-text-secondary)" />}
          label={t`Timezone`}
          value={user.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
        />
        <InfoCard icon={<Mail className="size-5 text-(--color-text-secondary)" />} label={t`Email`} value={user.email} />
        <InfoCard
          icon={<CalendarDays className="size-5 text-(--color-text-secondary)" />}
          label={t`Last updated`}
          value={safeFormatDate(user.updatedAt, "dd MMM yyyy")}
        />
        <InfoCard
          icon={<Globe className="size-5 text-(--color-text-secondary)" />}
          label={t`Regions`}
          value={regionNames.length > 0 ? regionNames.join(", ") : "-"}
        />
        <InfoCard
          icon={<MapPin className="size-5 text-(--color-text-secondary)" />}
          label={t`Locations`}
          value={locationNames.length > 0 ? locationNames.join(", ") : "-"}
        />
        <InfoCard
          icon={<List className="size-5 text-(--color-text-secondary)" />}
          label={t`Recipes created`}
          value={
            <span>
              <span className="text-base font-medium text-(--color-text-primary)">{recipesCreated} </span>
              <span className="text-sm font-normal text-(--color-text-secondary)">{t`recipes`}</span>
            </span>
          }
        />
        <InfoCard
          icon={<Lock className="size-5 text-(--color-text-secondary)" />}
          label={t`Permissions`}
          value={permissionsSummary}
        />
      </div>

      <div className="h-px bg-(--shell-border)" />

      <div
        role="tablist"
        aria-label={t`User sections`}
        className="flex w-full flex-wrap gap-1 rounded-md bg-(--color-surface-muted) p-1"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "rounded-md px-3 py-2 text-sm transition-colors",
              activeTab === tab.id
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-secondary)",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "general" && (
        <GeneralDetailsSection
          user={user}
          role={role}
          canEditUser={canEditUser}
          onEdit={() => setEditOpen(true)}
        />
      )}
      {activeTab === "access" && (
        <UserAccessControlTab
          userId={userIdNum}
          organization={user.organization}
          locations={locations}
          roleAssignments={roles}
          canEdit={canEditPermissions}
        />
      )}
      {activeTab === "activity" && (
        <ActivityLogTable scope={{ userId: userIdNum }} />
      )}
      {activeTab === "resetPassword" && (
        <UserResetPasswordTab userId={userIdNum} />
      )}
      {activeTab !== "general" && activeTab !== "access" && activeTab !== "activity" && activeTab !== "resetPassword" && (
        <div className="py-14 text-center text-sm text-(--color-text-secondary)">{t`Coming soon`}</div>
      )}

      <EditUserDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
        roleAssignments={roles}
      />
    </div>
  );
}

/**
 * The API stores an address as separate columns — there is no single `address`
 * field, which is why this row used to render "-" for everyone.
 */
function formatUserAddress(user: User): string {
  const cityLine = [user.city, user.state].filter(Boolean).join(", ");
  const parts = [
    user.addressLine1,
    user.addressLine2,
    [cityLine, user.postalCode].filter(Boolean).join(" "),
  ].filter((part) => part && part.trim());

  return parts.length ? parts.join(", ") : "-";
}

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="rounded-[10px] border border-(--shell-border) bg-(--color-surface-content) p-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-(--shell-border)">
            {icon}
          </div>
          <span className="text-sm font-medium text-(--color-text-secondary)">{label}</span>
        </div>
        <div className="truncate text-base font-medium text-(--color-text-primary)">{value}</div>
      </div>
    </div>
  );
}

function CopyValue({ value }: { value: string }) {
  const { t } = useLingui();
  const copy = () => {
    void navigator.clipboard.writeText(value);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
      aria-label={t`Copy value`}
    >
      <Copy className="size-4" />
    </button>
  );
}

function DetailRow({
  label,
  value,
  helperText,
}: {
  label: string;
  value: ReactNode;
  helperText?: string;
}) {
  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
        <div className="w-full shrink-0 md:w-85">
          <p className="text-sm font-medium text-(--color-text-primary)">{label}</p>
          {helperText ? <p className="mt-1 text-xs text-(--color-text-secondary)">{helperText}</p> : null}
        </div>
        <div className="flex-1">{value}</div>
      </div>
      <div className="h-px bg-(--shell-border)" />
    </>
  );
}

function GeneralDetailsSection({
  user,
  role,
  canEditUser,
  onEdit,
}: {
  user: User;
  role: string;
  canEditUser: boolean;
  onEdit: () => void;
}) {
  const { t } = useLingui();

  return (
    <div className="px-4">
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium leading-7 text-(--color-text-primary)">{t`General Details`}</h2>
            <p className="text-sm leading-5 text-(--color-text-secondary)">
              {t`View the core profile and contact details for this user.`}
            </p>
          </div>
          <Button
            variant="outline"
            className="h-10 shrink-0 gap-2 rounded-[10px] border-(--shell-border) px-4"
            onClick={onEdit}
            disabled={!canEditUser}
          >
            <SquarePen className="size-4" />
            <span className="text-sm font-medium text-(--color-text-primary)">{t`Edit`}</span>
          </Button>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      <div className="mt-5 flex flex-col gap-5">
        <DetailRow
          label={t`Name`}
          value={<span className="text-base font-medium text-(--color-text-primary)">{getUserName(user)}</span>}
        />

        <DetailRow
          label={t`Birth date`}
          value={<span className="text-base font-medium text-(--color-text-primary)">{safeFormatDate(user.birthDate, "dd.MM.yyyy")}</span>}
        />

        <DetailRow
          label={t`Role`}
          value={<span className="text-base font-medium text-(--color-text-primary)">{role}</span>}
        />

        <DetailRow
          label={t`Status`}
          value={
            <Badge
              className={cn(
                "rounded-full border-transparent px-3 py-1 text-sm font-medium",
                user.isActive
                  ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                  : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
              )}
            >
              {user.isActive ? t`Active` : t`Inactive`}
            </Badge>
          }
        />

        <DetailRow
          label={t`Contacts`}
          helperText={t`Primary channels for reaching this user.`}
          value={
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-(--color-surface-accent)">
                  <Mail className="size-5 text-(--color-text-secondary)" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-(--color-text-secondary)">{t`Email`}</p>
                  <div className="flex items-center gap-2">
                    <p className="truncate text-base font-medium text-(--color-text-primary)">{user.email}</p>
                    <CopyValue value={user.email} />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-(--color-surface-accent)">
                  <Phone className="size-5 text-(--color-text-secondary)" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-(--color-text-secondary)">{t`Phone`}</p>
                  <div className="flex items-center gap-2">
                    <p className="truncate text-base font-medium text-(--color-text-primary)">{user.phone ?? "-"}</p>
                    {user.phone ? <CopyValue value={user.phone} /> : null}
                  </div>
                </div>
              </div>
            </div>
          }
        />

        <DetailRow
          label={t`Profile image`}
          helperText={t`The user has not uploaded an avatar yet.`}
          value={<span className="text-base font-medium text-(--color-text-secondary)">{t`No profile image is set by user.`}</span>}
        />

        <DetailRow
          label={t`Address`}
          value={<span className="text-base font-medium text-(--color-text-primary)">{formatUserAddress(user)}</span>}
        />

        <DetailRow
          label={t`Country`}
          value={<span className="text-base font-medium text-(--color-text-primary)">{user.country ?? "-"}</span>}
        />

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
          <p className="w-full shrink-0 text-sm font-medium text-(--color-text-primary) md:w-85">{t`Timezone`}</p>
          <p className="text-base font-medium text-(--color-text-primary)">
            {user.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
          </p>
        </div>
      </div>
    </div>
  );
}
